import * as React from 'react';
import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import MenuIcon from '@mui/icons-material/Menu';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ColorModeIconDropdown from '../theme/ColorModeIconDropdown';
import RewardiouLogo from './RewardiouLogo';
import { useAppContext } from '../contexts/AppContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Login, Logout, PersonAdd } from '@mui/icons-material';
import { Link } from 'react-scroll';
import { useNavigate } from 'react-router-dom';
import { Select, MenuItem as MuiMenuItem, ListItemText, Typography } from '@mui/material';

// @ts-ignore
import Flag from "react-world-flags";
import createAuthService from '../utils/AuthService';

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
  backdropFilter: 'blur(24px)',
  border: '1px solid',
  borderColor: theme.palette.divider,
  backgroundColor: alpha(theme.palette.background.default, 0.4),
  boxShadow: theme.shadows[1],
  padding: '8px 12px',
}));

interface AppAppBarProps {
  hideNavigation?: boolean;
  hideAuthButtons?: boolean;
  hideNavigationOnly?: boolean;
}

export default function AppAppBar({ hideNavigation = false, hideAuthButtons = false, hideNavigationOnly = false }: AppAppBarProps) {
  const [open, setOpen] = React.useState(false);
  const { appState, updateAppState } = useAppContext();
  const { language, setLanguage, translations } = useLanguage();
  const navigate = useNavigate();
  let authService = createAuthService({ appState, updateAppState });
  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  const handleLogOut = () => {
    updateAppState({
      isAuthenticated: false,
      accessToken: '',
      refreshToken: '',
      user: null,
    });
    navigate('/');
  };

  const handleLanguageChange = async (languagecode: string) => {
    setLanguage(languagecode);
    await authService.updateLanguageAsync(languagecode);
  }

  return (
    <AppBar
      position="fixed"
      enableColorOnDark
      sx={{
        boxShadow: 0,
        bgcolor: 'transparent',
        backgroundImage: 'none',
        mt: 'calc(var(--template-frame-height, 0px) + 28px)',
      }}
    >
      <Container maxWidth="lg">
        <StyledToolbar variant="dense" disableGutters>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', px: 0 }}>
            <RewardiouLogo />
            {!hideNavigation && !hideNavigationOnly && !appState.isAuthenticated && (
              <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                <Link to="features" smooth={true} duration={500}>
                  <Button variant="text" color="info" size="small">
                    {translations.features}
                  </Button>
                </Link>
                <Link to="testimonials" smooth={true} duration={500}>
                  <Button variant="text" color="info" size="small">
                    {translations.testimonials}
                  </Button>
                </Link>
                <Link to="highlights" smooth={true} duration={500}>
                  <Button variant="text" color="info" size="small">
                    {translations.highlights}
                  </Button>
                </Link>
                <Link to="pricing" smooth={true} duration={500}>
                  <Button variant="text" color="info" size="small">
                    {translations.pricing}
                  </Button>
                </Link>
                <Link to="faq" smooth={true} duration={500}>
                  <Button variant="text" color="info" size="small" sx={{ minWidth: 0 }}>
                    {translations.faq}
                  </Button>
                </Link>
                <Link to="contact" smooth={true} duration={500}>
                  <Button variant="text" color="info" size="small">
                    {translations.contact_us}
                  </Button>
                </Link>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              gap: 1,
              alignItems: 'center',
            }}
          >
            {/* 🌍 Language Selection Dropdown with Flags */}
            <Select
              value={language}
              onChange={(e) => handleLanguageChange(e.target.value)}
              size="small"
              sx={{ minWidth: 120, bgcolor: 'background.paper', color: 'text.primary', borderRadius: 1 }}
            >
              <MuiMenuItem value="en">
                <Flag code="GB" style={{ width: 25, height: 15, marginRight: 8 }} />
                <ListItemText primary="English" />
              </MuiMenuItem>
              <MuiMenuItem value="pt">
                <Flag code="PT" style={{ width: 25, height: 15, marginRight: 8 }} />
                <ListItemText primary="Português" />
              </MuiMenuItem>
              <MuiMenuItem value="de">
                <Flag code="DE" style={{ width: 25, height: 15, marginRight: 8 }} />
                <ListItemText primary="Deutch" />
              </MuiMenuItem>
              <MuiMenuItem value="tr">
                <Flag code="TR" style={{ width: 25, height: 15, marginRight: 8 }} />
                <ListItemText primary="Türkçe" />
              </MuiMenuItem>
            </Select>


            {!hideAuthButtons &&
              (!appState.isAuthenticated ? (
                <>
                  <Button onClick={() => navigate('/signin')} color="primary" variant="outlined" size="small" endIcon={<Login />}>
                    {translations.signin}
                  </Button>
                  <Button onClick={() => navigate('/signup')} color="primary" variant="outlined" size="small" endIcon={<PersonAdd />}>
                    {translations.signup}
                  </Button>
                </>
              ) : (
                <Button onClick={() => handleLogOut()} color="primary" variant="outlined" size="small" endIcon={<Logout />}>
                  {translations.logout}
                </Button>
              ))}
            <ColorModeIconDropdown />
          </Box>

          <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1 }}>
            <ColorModeIconDropdown size="medium" />
            <IconButton aria-label="Menu button" onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="top"
              open={open}
              onClose={toggleDrawer(false)}
              PaperProps={{
                sx: {
                  top: 'var(--template-frame-height, 0px)',
                },
              }}
            >
              <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton onClick={toggleDrawer(false)}>
                    <CloseRoundedIcon />
                  </IconButton>
                </Box>

                {!hideNavigation && !hideNavigationOnly && (
                  <>
                    <MenuItem>{translations.features}</MenuItem>
                    <MenuItem>{translations.testimonials}</MenuItem>
                    <MenuItem>{translations.highlights}</MenuItem>
                    <MenuItem>{translations.pricing}</MenuItem>
                    <MenuItem>{translations.faq}</MenuItem>
                    <MenuItem>{translations.blog}</MenuItem>
                    <Divider sx={{ my: 3 }} />
                  </>
                )}

                {/* Added language selector to mobile drawer */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Language
                  </Typography>
                  <Select
                    value={language}
                    onChange={(e) => handleLanguageChange(e.target.value)}
                    size="small"
                    fullWidth
                    sx={{ bgcolor: 'background.paper', color: 'text.primary', borderRadius: 1 }}
                  >
                    <MuiMenuItem value="en">
                      <Flag code="GB" style={{ width: 25, height: 15, marginRight: 8 }} />
                      <ListItemText primary="English" />
                    </MuiMenuItem>
                    <MuiMenuItem value="pt">
                      <Flag code="PT" style={{ width: 25, height: 15, marginRight: 8 }} />
                      <ListItemText primary="Português" />
                    </MuiMenuItem>
                    <MuiMenuItem value="de">
                      <Flag code="DE" style={{ width: 25, height: 15, marginRight: 8 }} />
                      <ListItemText primary="Deutch" />
                    </MuiMenuItem>
                    <MuiMenuItem value="tr">
                      <Flag code="TR" style={{ width: 25, height: 15, marginRight: 8 }} />
                      <ListItemText primary="Türkçe" />
                    </MuiMenuItem>
                  </Select>
                </Box>

                {/* Added auth buttons to mobile drawer */}
                {!hideAuthButtons && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {!appState.isAuthenticated ? (
                      <>
                        <Button
                          onClick={() => {
                            navigate('/signin');
                            toggleDrawer(false)();
                          }}
                          color="primary"
                          variant="outlined"
                          fullWidth
                          endIcon={<Login />}
                        >
                          {translations.signin}
                        </Button>
                        <Button
                          onClick={() => {
                            navigate('/signup');
                            toggleDrawer(false)();
                          }}
                          color="primary"
                          variant="outlined"
                          fullWidth
                          endIcon={<PersonAdd />}
                        >
                          {translations.signup}
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => {
                          handleLogOut();
                          toggleDrawer(false)();
                        }}
                        color="primary"
                        variant="outlined"
                        fullWidth
                        endIcon={<Logout />}
                      >
                        {translations.logout}
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            </Drawer>
          </Box>
        </StyledToolbar>
      </Container>
    </AppBar>
  );
}